import React, { FC, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { format } from 'date-fns'
import './group-cell.css'

// COMPONENTS
import * as Icons from '@icons'
import ProgressBar from '@components/atoms/progressbar'

// UTILITIES
import { priceFormatter } from '@utils/number-helper'
import { isNonEmptyString } from '@utils/type-checkers'


interface Props extends React.HTMLAttributes<HTMLDivElement> {
    group: Group
}

// const defaultSlotsObject: { openSlots: Pledge[], claimedSlots: Pledge[] } = { openSlots: [], claimedSlots: [] }
// ToDo: Group Statuses
const GroupCell: FC<Props> = (props) => {
    const { className, group, ...divProps } = props
    const { card, id: groupId, organizer, title } = group
    const { isSignedIn, user } = useUser()
    const navigate = useNavigate()

    const ledgerTotal = useMemo(() => {
        if (!group.ledger) return 0
        return group.ledger.reduce(
            (transactionSum, transaction) => transactionSum + transaction.breakdown.reduce((sum, item) => sum + item.amount, 0), 0
        )
    }, [group.ledger])

    const isOrganizer = isSignedIn ? organizer?.id === user.id : false
    const isMerchant = !!user?.publicMetadata.merchant && !!group.merchant && user?.publicMetadata.merchant?.id === group.merchant?.id
    const organizerName = `${organizer?.givenName || ''} ${organizer?.familyName || ''}`.trim()

    const isLocked = group.closedAt !== "0001-01-01T00:00:00Z"
    const isCompleted = (group.completedAt !== "0001-01-01T00:00:00Z" && ((ledgerTotal === group.spendAmount) || (!isOrganizer && !isMerchant))) || group.finalizedAt !== '0001-01-01T00:00:00Z'
    const isPending = group.completedAt !== "0001-01-01T00:00:00Z" && group.finalizedAt === '0001-01-01T00:00:00Z' && (ledgerTotal !== group.spendAmount) && (isOrganizer || isMerchant)
    const isFinalized = group.finalizedAt !== "0001-01-01T00:00:00Z"
    const isCanceled = group.canceledAt !== "0001-01-01T00:00:00Z"
    const isActivated = !isCompleted && !isCanceled && isLocked
    const isFunding = !isCompleted && !isCanceled && !isActivated && !!group.collectedAmount && !!group.targetAmount && (group.collectedAmount < group.targetAmount)
    const isFunded = !isCompleted && !isCanceled && !isActivated && !!group.collectedAmount && !!group.targetAmount && (group.collectedAmount >= group.targetAmount)

    const { userPledges, claimedPledges } = useMemo(() => [...(group.pledges || [])].reduce<{ userPledges: Pledge[], claimedPledges: Pledge[] }>((prev, pledge) => {
        if (isSignedIn && isNonEmptyString(user.id)) {
            if (pledge.user?.id === user.id) return { ...prev, userPledges: [...prev.userPledges, pledge], claimedPledges: [...prev.claimedPledges, pledge] };
            else return { ...prev, claimedPledges: (!!pledge.transactionId || !!pledge.user?.id) ? [...prev.claimedPledges, pledge] : prev.claimedPledges }
        } else return prev
    }, { userPledges: [], claimedPledges: [] }), [isSignedIn, user?.id, group.pledges])

    const remainingFunds = useMemo(() => {
        const closedAmount = (group.pledges || [])
            .filter(p => p.closedAt !== "0001-01-01T00:00:00Z")
            .map(p => p.amount - p.chargedToCard)
            .reduce((a, b) => a + b, 0)

        const amount = (group.collectedAmount || 0) - closedAmount - (group.spendAmount || 0)
        if (amount === 0)
            return 0

        return amount / 100
    }, [group.collectedAmount, group.spendAmount, group.pledges])

    const { pendingTransactionCount, allChargesFinalized } = useMemo(() => {
        if (!group.transactions) return { pendingTransactionCount: 0, allChargesFinalized: false }

        const transactionGroups = Object.values(group.transactions)
        if (transactionGroups.length === 0) return { pendingTransactionCount: 0, allChargesFinalized: false }

        let pendingCount = 0
        let finalizedCount = 0

        for (const transactions of transactionGroups) {
            const latestStatus = transactions.at(-1)?.status
            if (latestStatus === 'pending') {
                pendingCount++
            } else if (latestStatus === 'finalized' || latestStatus === 'Credit') {
                finalizedCount++
            }
        }

        return {
            pendingTransactionCount: pendingCount,
            allChargesFinalized: finalizedCount > 0 && pendingCount === 0
        }
    }, [group.transactions])

    function handleClick() {
        navigate(`/group/${groupId}`)
    }

    return (
        <div className={className} {...divProps}>
            {(!isCanceled) && (<>
                {(!isCompleted || isPending) && (<>
                    {(isFunding || isFunded) ? (<>
                        <div className='group-row cursor-pointer' onClick={handleClick}>
                            {(isOrganizer || isMerchant) ? (<>
                                <div className='flex flex-col gap-2.5 w-full'>
                                    <div className='flex flex-row items-end justify-between'>
                                        <div className='flex flex-col gap-1'>
                                            <h4 className='text-black'><strong>{title}</strong></h4>
                                            <p className='text-body-2'><span className='text-black font-bold'>{priceFormatter.format((group.collectedAmount || 0) / 100)}</span> Raised of <span className='text-black font-bold'>{priceFormatter.format((group.targetAmount || 0) / 100)}</span> Goal</p>
                                        </div>
                                        <div className='flex flex-col items-end justify-between gap-1'>
                                            {isFunded && <p className='text-body-2 text-green-700'><strong>Activate</strong></p>}
                                            <div className="flex flex-col items-center justify-center bg-blue-500 text-white rounded-full w-8 h-8">
                                                <Icons.Arrow width=".875rem" height="0.875rem" strokeWidth={'2px'} facing="right" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex flex-col gap-2.5'>
                                        <div className='w-full h-2'>
                                            <ProgressBar.Default
                                                value={Math.min(((group.collectedAmount || 0) / 100), ((group.targetAmount || 0) / 100))}
                                                max={(group.targetAmount || 0) / 100}
                                                settings={{ fillColor: isFunded ? 'var(--splitcard-green-700)' : 'var(--splitcard-blue-500)' }}
                                                className='h-full'
                                            />
                                        </div>
                                        <div className='flex flex-row justify-between'>
                                            <p className='text-body-2'>{isFunded ? `${claimedPledges} Joined` : 'Organized by You'}</p>
                                            <p className='text-body-2 text-right'>Created: {!!group.createdAt && format(group.createdAt, "MM/dd/yy")}</p>
                                        </div>
                                    </div>
                                </div>
                            </>) : (<>
                                <div className='flex flex-col gap-2.5 w-full'>
                                    <div className='flex flex-row items-end justify-between'>
                                        <div className='flex flex-col gap-1'>
                                            <h4 className='text-black'><strong>{title}</strong></h4>
                                            <p className='text-body-2'><span className='text-black font-bold'>{priceFormatter.format((group.collectedAmount || 0) / 100)}</span> Raised of <span className='text-black font-bold'>{priceFormatter.format((group.targetAmount || 0) / 100)}</span> Goal</p>
                                        </div>
                                        <div className='flex flex-col items-end justify-between'>
                                            <div className="flex flex-col items-center justify-center bg-blue-500 text-white rounded-full w-8 h-8">
                                                <Icons.Arrow width=".875rem" height="0.875rem" strokeWidth={'2px'} facing="right" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex flex-col gap-2.5'>
                                        <div className='w-full h-2'>
                                            <ProgressBar.Default
                                                value={Math.min(((group.collectedAmount || 0) / 100), ((group.targetAmount || 0) / 100))}
                                                max={(group.targetAmount || 0) / 100}
                                                settings={{ fillColor: isFunded ? 'var(--splitcard-green-700)' : 'var(--splitcard-blue-500)' }}
                                                className='h-full'
                                            />
                                        </div>
                                        <div className='flex flex-row justify-between'>
                                            <p className='text-body-2'>{isFunded ? `${claimedPledges} Joined` : `Organized by ${organizerName}`}</p>
                                            <p className='text-body-2 text-right'>Created: {!!group.createdAt && format(group.createdAt, "MM/dd/yy")}</p>
                                        </div>
                                    </div>
                                </div>
                            </>)}
                        </div>
                    </>) : (<>
                        <div className='group-grid active-group cursor-pointer' onClick={handleClick}>
                            <div>
                                <h2>{title}</h2>
                                {!isOrganizer && <p className='text-body-2'>Organized by <strong>{organizerName}</strong></p>}
                            </div>
                            {(isPending && (isOrganizer || isMerchant)) ? (
                                <div className="label">
                                    <div className='flex flex-row items-center gap-2' style={{
                                        backgroundColor: allChargesFinalized ? 'rgba(34, 197, 94, 0.10)' : 'rgba(254, 154, 0, 0.10)',
                                        border: `1px solid ${allChargesFinalized ? 'rgba(34, 197, 94, 0.20)' : 'rgba(254, 154, 0, 0.20)'}`,
                                        borderRadius: 'var(--border-radius-md)',
                                        padding: '0.25rem 0.75rem',
                                        color: allChargesFinalized ? 'var(--splitcard-green-500)' : '#FF9500',
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold'
                                    }}>
                                        <Icons.Warning width="1.25rem" height="1.25rem" strokeWidth="2px" style={{ color: '#FF9500', flexShrink: 0 }} />
                                        <p className="text-body-2" style={{ color: '#FF9500' }}><strong>ACTION REQUIRED</strong></p>
                                    </div>
                                </div>
                            ) : (allChargesFinalized || pendingTransactionCount > 0) && (
                                <div className='label'>
                                    <div className='flex flex-row items-center gap-2' style={{
                                        backgroundColor: allChargesFinalized ? 'rgba(34, 197, 94, 0.10)' : 'rgba(254, 154, 0, 0.10)',
                                        border: `1px solid ${allChargesFinalized ? 'rgba(34, 197, 94, 0.20)' : 'rgba(254, 154, 0, 0.20)'}`,
                                        borderRadius: 'var(--border-radius-md)',
                                        padding: '0.25rem 0.75rem',
                                        color: allChargesFinalized ? 'var(--splitcard-green-500)' : '#FF9500',
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold'
                                    }}>
                                        {allChargesFinalized
                                            ? <><Icons.Check width="1rem" height="1rem" /> CHARGES FINALIZED</>
                                            : <><span className='text-xs'>●</span> {pendingTransactionCount} PENDING</>}
                                    </div>
                                </div>
                            )}
                            <div className='flex flex-col balance'>
                                {(isOrganizer && card?.cardType === 'physical') &&
                                    <div className='flex flex-row items-center gap-2 mb-2'>
                                        <img src="/icons/physical-card.svg" />
                                        <p className="text-body-2">Linked to physical card</p>
                                    </div>
                                }
                                {(isOrganizer && (card?.id === user?.publicMetadata.tera?.virtualCard?.id)) &&
                                    <div className='flex flex-row items-center gap-2 mb-2'>
                                        <img src="/icons/physical-card.svg" />
                                        <p className="text-body-2">Linked to reloadable card</p>
                                    </div>
                                }
                                {(!!userPledges.length && userPledges[0].closedAt !== '0001-01-01T00:00:00Z')
                                    ? <div>
                                        <p className='text-body text-red-600'><strong>You Have Left This Group</strong></p>
                                        <p className="text-body">Your Final Charge</p>
                                        <h3>{priceFormatter.format((userPledges[0]?.chargedToCard || 0) / 100)}</h3>
                                    </div>
                                    : <div>
                                        <p className="text-body-2">Available Funds:</p>
                                        <h2>{priceFormatter.format(remainingFunds)}</h2>
                                    </div>
                                }
                            </div>
                            <div className="status-flag">
                                <div className='flex flex-col items-center self-end'>
                                    <img className="payment-brand-img h-8" src="/payment-brands/mastercard-icon.svg" draggable="false" />
                                    <p className='text-body-2'>mastercard</p>
                                </div>
                            </div>
                        </div>
                    </>)}
                </>)}
                {isCompleted && (
                    <div className='group-grid completed-group cursor-pointer' onClick={handleClick}>
                        <div>
                            <h3>{title}</h3>
                            <p className='text-body'>Organized by <strong>{isOrganizer ? 'You' : `${organizerName}`}</strong></p>
                        </div>
                        <div className="status-flag">
                            <Icons.Check type="bordered" height="1.5rem" width="1.5rem" style={{ alignSelf: 'flex-start', color: 'var(--splitcard-green-500)' }} />
                            <div className='ml-3 text-right'>
                                <h3>Completed</h3>
                                {(!!group.completedAt) && (<>
                                    <p className='text-body-2'>{format(group.completedAt, "MM/dd/yy")}</p>
                                </>)}
                            </div>
                        </div>
                        <div>
                            <p className="text-body">Your {!isFinalized ? '' : 'Final'} Charge</p>
                            <h3>{priceFormatter.format((userPledges[0]?.chargedToCard || 0) / 100)}</h3>
                        </div>
                        <div className='link'>
                            {(allChargesFinalized || pendingTransactionCount > 0) && (
                                <div className='label'>
                                    <div className='flex flex-row items-center gap-2' style={{
                                        backgroundColor: allChargesFinalized ? 'rgba(34, 197, 94, 0.10)' : 'rgba(254, 154, 0, 0.10)',
                                        border: `1px solid ${allChargesFinalized ? 'rgba(34, 197, 94, 0.20)' : 'rgba(254, 154, 0, 0.20)'}`,
                                        borderRadius: 'var(--border-radius-md)',
                                        padding: '0.25rem 0.75rem',
                                        color: allChargesFinalized ? 'var(--splitcard-green-500)' : '#FF9500',
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold'
                                    }}>
                                        {allChargesFinalized
                                            ? <><Icons.CheckSmall width="1rem" height="1rem" /> CHARGES FINALIZED</>
                                            : <><span className='text-xs'>●</span> {pendingTransactionCount} PENDING</>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </>)}
            {(isCanceled) && (
                <div className='group-row cursor-pointer' onClick={handleClick}>
                    <div className='flex flex-col w-2/5'>
                        <h3>{title}</h3>
                        <p className='text-body-2'>Organized by <strong>{isOrganizer ? 'You' : `${organizerName}`}</strong></p>
                    </div>
                    <div className="flex flex-row items-center">
                        <div className='ml-3'>
                            <h3>Canceled</h3>
                            {(!!group.canceledAt) && (<>
                                <p className='text-body-2'>{format(group.canceledAt, "MM/dd/yy")}</p>
                            </>)}
                        </div>
                    </div>
                    <div className='link'>
                        <div className="flex flex-col items-center justify-center bg-blue-500 text-white rounded-full w-8 h-8">
                            <Icons.Arrow width=".875rem" height=".875rem" strokeWidth={'2px'} facing="right" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default GroupCell
